import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fraudService, isNegConfTerminal, type NegativeConfirmation } from '../../fraud/services/fraud'
import EnquiryTrackingCard from '../../leads/components/EnquiryTrackingCard'
import { ShieldOff, X, History, ChevronDown, Eye, CheckCircle, ShieldAlert } from 'lucide-react'
import { useWindowWidth } from '../../../lib/useWindowWidth'
import { ModalOverlay } from '../../../components/ModalOverlay'

function timeAgo(ts: string): string {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000)
  if (s < 60)    return 'just now'
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function statusBadge(nc: NegativeConfirmation) {
  if (nc.investigation_status === 'FRAUD_ESTABLISHED')
    return <span className="badge-orange" style={{ fontSize: 10 }}>Fraud Established</span>
  if (nc.investigation_status === 'RESOLVED')
    return <span className="badge-success" style={{ fontSize: 10 }}>Resolved</span>
  if (nc.investigation_status === 'INVESTIGATING')
    return <span className="badge-warning" style={{ fontSize: 10 }}>Investigating</span>
  return <span className="badge-danger" style={{ fontSize: 10 }}>Open</span>
}

export default function NegativeConfirmationsPage() {
  const [items, setItems]           = useState<NegativeConfirmation[]>([])
  const [loading, setLoading]       = useState(true)
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const [actionModal, setActionModal] = useState<{ id: number; mode: 'resolve' | 'fraud' } | null>(null)
  const [actionNote, setActionNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isMobile = useWindowWidth() < 768
  const navigate = useNavigate()

  const load = () =>
    fraudService.listNegativeConfirmations()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false))

  useEffect(() => { load() }, [])

  const visible = items.filter(n => n.customer_response === 'DISPUTED')

  const handleInvestigate = async (id: number) => {
    await fraudService.investigateNegConf(id)
    load()
  }

  const handleSubmitAction = async () => {
    if (!actionModal || !actionNote.trim()) return
    setSubmitting(true)
    try {
      if (actionModal.mode === 'resolve') {
        await fraudService.resolveNegConf(actionModal.id, actionNote)
      } else {
        await fraudService.markFraudNegConf(actionModal.id, actionNote)
      }
      setActionModal(null)
      setActionNote('')
      load()
    } finally {
      setSubmitting(false)
    }
  }

  const ActionButtons = ({ nc }: { nc: NegativeConfirmation }) => (
    <div className="flex items-center gap-1 flex-wrap">
      <button
        className="btn-icon"
        title="View call history"
        onClick={() => setSelectedLeadId(nc.lead_id)}
        style={{ color: 'var(--text-tertiary)' }}
      >
        <History className="h-4 w-4" />
      </button>
      {!isNegConfTerminal(nc.investigation_status) && nc.investigation_status !== 'INVESTIGATING' && (
        <button
          className="btn-secondary"
          style={{ fontSize: 12, height: 28, padding: '0 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          onClick={() => handleInvestigate(nc.id)}
        >
          <Eye className="h-3.5 w-3.5" /> Investigate
        </button>
      )}
      {nc.investigation_status === 'INVESTIGATING' && (
        <>
          <button
            className="btn-primary"
            style={{ fontSize: 12, height: 28, padding: '0 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => { setActionModal({ id: nc.id, mode: 'resolve' }); setActionNote('') }}
          >
            <CheckCircle className="h-3.5 w-3.5" /> Resolve
          </button>
          <button
            className="btn-danger"
            style={{ fontSize: 12, height: 28, padding: '0 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => { setActionModal({ id: nc.id, mode: 'fraud' }); setActionNote('') }}
          >
            <ShieldAlert className="h-3.5 w-3.5" /> Mark Fraud
          </button>
        </>
      )}
      {!isNegConfTerminal(nc.investigation_status) && (
        <a
          href={`/leads/${nc.lead_id}`}
          className="btn-secondary"
          style={{ fontSize: 12, height: 28, padding: '0 8px', display: 'inline-flex', alignItems: 'center' }}
        >
          View Lead
        </a>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ShieldOff className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />
            Negative Confirmations
          </h1>
          <p className="page-subtitle">
            Customers who disputed a policy decline — requires investigation
          </p>
        </div>
        <button
          className="btn-secondary hidden sm:flex"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="spinner" />
        </div>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <ShieldOff className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p>No disputed confirmations</p>
        </div>
      ) : isMobile ? (
        <div className="flex flex-col gap-2">
          {visible.map(nc => {
            const expanded = expandedId === nc.id
            return (
              <div key={nc.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div
                  className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                  onClick={() => setExpandedId(expanded ? null : nc.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--brand-primary)' }}>
                      {nc.customer_name || `Lead #${nc.lead_id}`}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {nc.vehicle_reg_no || '—'}{nc.telecaller_name ? ` · ${nc.telecaller_name}` : ''}
                    </p>
                  </div>
                  {statusBadge(nc)}
                  <ChevronDown
                    className="h-4 w-4 shrink-0 transition-transform"
                    style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                  />
                </div>
                {expanded && (
                  <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                    {nc.telecaller_name && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Telecaller</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{nc.telecaller_name}</span>
                      </div>
                    )}
                    {nc.responded_at && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Disputed</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{timeAgo(nc.responded_at)}</span>
                      </div>
                    )}
                    {nc.resolution_note && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Resolution</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{nc.resolution_note}</span>
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      <ActionButtons nc={nc} />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Vehicle</th>
                  <th>Telecaller</th>
                  <th>Disputed</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(nc => (
                  <tr key={nc.id}>
                    <td>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {nc.customer_name || '—'}
                      </span>
                    </td>
                    <td className="text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                      {nc.vehicle_reg_no || '—'}
                    </td>
                    <td className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {nc.telecaller_name || '—'}
                    </td>
                    <td className="text-sm" style={{ color: 'var(--text-secondary)' }}
                        title={nc.responded_at ? new Date(nc.responded_at).toLocaleString() : ''}>
                      {nc.responded_at ? timeAgo(nc.responded_at) : '—'}
                    </td>
                    <td>{statusBadge(nc)}</td>
                    <td>
                      <ActionButtons nc={nc} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resolve / Mark Fraud Modal */}
      {actionModal !== null && (
        <ModalOverlay onClose={() => { setActionModal(null); setActionNote('') }}>
          <div className="modal-panel" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                {actionModal.mode === 'resolve' ? 'Resolve Dispute' : 'Mark Fraud Established'}
              </h2>
              <button className="btn-icon" onClick={() => { setActionModal(null); setActionNote('') }}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="form-group">
                <label className="form-label">Resolution Note <span style={{ color: 'var(--danger-text)' }}>*</span></label>
                <textarea
                  className="input"
                  rows={3}
                  value={actionNote}
                  onChange={e => setActionNote(e.target.value)}
                  placeholder={
                    actionModal.mode === 'resolve'
                      ? 'e.g. Verified with telecaller — customer did not pay us. Policy issued elsewhere.'
                      : 'e.g. Customer confirmed via call recording they never paid — fraudulent dispute.'
                  }
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button className="btn-secondary" onClick={() => { setActionModal(null); setActionNote('') }}>
                  Cancel
                </button>
                <button
                  className={actionModal.mode === 'resolve' ? 'btn-primary' : 'btn-danger'}
                  onClick={handleSubmitAction}
                  disabled={submitting || !actionNote.trim()}
                >
                  {actionModal.mode === 'resolve' ? <CheckCircle className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
                  {submitting ? 'Saving…' : actionModal.mode === 'resolve' ? 'Mark Resolved' : 'Mark Fraud Established'}
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* EnquiryTrackingCard slide-over */}
      {selectedLeadId !== null && (
        <>
          <div
            className="modal-overlay"
            onClick={() => setSelectedLeadId(null)}
            style={{ zIndex: 40 }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(480px, 100vw)',
              zIndex: 50,
              background: 'var(--surface-card)',
              boxShadow: '-8px 0 40px rgba(0,0,0,0.35)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              className="flex items-center justify-between px-5 py-4 shrink-0"
              style={{ borderBottom: '1px solid var(--border-default)' }}
            >
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Lead #{selectedLeadId} — History
              </p>
              <button className="btn-icon" onClick={() => setSelectedLeadId(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <EnquiryTrackingCard leadId={selectedLeadId} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
